import React, { useState, useEffect } from 'react';
import { Row, Col, Form, FloatingLabel, Button } from 'react-bootstrap';
import { Select } from '../../../../components/components/Select/Select';
import { Label } from '../../../../components';
import { CarrierAddress } from '../../Model/CarrierAddresses';
import LookupService from '../../../../Services/LookupService';
import { CarrierFormMode } from './CarrierDetailsForm';
import CarrierContactInfoSection from './CarrierContactInfoSection';
import * as constant from '../../../../shared/util/constant';

interface CarrierAddressListProps {
  mode: CarrierFormMode;
  addresses: CarrierAddress[];
  onChange: (addresses: CarrierAddress[]) => void;
  canCreate?: boolean;
  canDelete?: boolean;
}

const CarrierAddressList: React.FC<CarrierAddressListProps> = ({ mode, addresses, onChange, canCreate = true, canDelete = true }) => {
  const isEditable = mode === 'edit' || mode === 'create';
  const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
  const [formValidation, setFormValidation] = useState<Record<number, any>>({});

  useEffect(() => {
    LookupService.fetchUSStates().then(data => {
      setStateOptions(data.map(item => ({ value: item.lookupCode, label: item.lookupValue })));
    });
  }, []);

  const handleAdd = () => {
    onChange([...addresses, { addrDesc: '', countryCode: '', address1: '', address2: '', city: '', state: '', zipCode: '', isForeign: false, isPaymentAddress: false, isFormAddress: false, isPrimaryAddress: false, isPayee: false, areaCode: '', phone: '', ext: '', faxAreaCode: '', faxCode: '', areaCodePhone24Hr: '', phone24Hr: '', email: '', companyURL: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(addresses.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, field: keyof CarrierAddress, value: string) => {
    onChange(addresses.map((addr, i) => (i === index ? { ...addr, [field]: value } : addr)));
    if (field === 'addrDesc') {
      setFormValidation(prev => {
        if (!value || !value.trim()) {
          return { ...prev, [index]: { addrDesc: { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK } } };
        }
        const { [index]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleFieldBlur = (index: number, field: string, value: string) => {
    if (field === 'addrDesc') {
      setFormValidation(prev => {
        if (!value || !value.trim()) {
          return { ...prev, [index]: { addrDesc: { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK } } };
        }
        return prev;
      });
    }
  };

  return (
    <>
      <Row>
        <Col md={12} className="mb-3">
          <div className="d-flex align-items-center justify-content-between">
            {addresses.length > 0 && <Label text="Carrier Addresses" className="roboto-condensed-bold" />}
            {isEditable && (
              <Button type="button" variant="outline-primary" size="sm" onClick={handleAdd} disabled={!canCreate}>
                + Add Address
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {addresses.map((addr, index) => (
        <React.Fragment key={index}>
          <Row>
            <Col md={6} className="mb-3">
              <FloatingLabel controlId={`addrDesc-${index}`} label={<span>Address Description<span className="text-danger ms-1">*</span></span>}>
                <Form.Control
                  type="text"
                  value={addr.addrDesc || ''}
                  onChange={e => handleFieldChange(index, 'addrDesc', e.target.value)}
                  onBlur={e => handleFieldBlur(index, 'addrDesc', e.target.value)}
                  disabled={!isEditable}
                  required
                  maxLength={25}
                  isInvalid={formValidation[index]?.addrDesc?.isValid === false}
                />
                <Form.Control.Feedback type="invalid">
                  {formValidation[index]?.addrDesc?.warningText}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={3} className="mb-3">
              <FloatingLabel controlId={`countryCode-${index}`} label="Country Code">
                <Form.Control
                  type="text"
                  value={addr.countryCode || ''}
                  onChange={e => {
                    const val = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase();
                    handleFieldChange(index, 'countryCode', val);
                  }}
                  disabled={!isEditable}
                  maxLength={2}
                />
              </FloatingLabel>
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <FloatingLabel controlId={`addr1-${index}`} label="Address 1">
                <Form.Control
                  type="text"
                  value={addr.address1 || ''}
                  onChange={e => handleFieldChange(index, 'address1', e.target.value)}
                  disabled={!isEditable}
                  maxLength={75}
                />
              </FloatingLabel>
            </Col>
            <Col md={6} className="mb-3">
              <FloatingLabel controlId={`addr2-${index}`} label="Address 2">
                <Form.Control
                  type="text"
                  value={addr.address2 || ''}
                  onChange={e => handleFieldChange(index, 'address2', e.target.value)}
                  disabled={!isEditable}
                  maxLength={75}
                />
              </FloatingLabel>
            </Col>
          </Row>
          <Row>
            <Col md={4} className="mb-3">
              <FloatingLabel controlId={`city-${index}`} label="City">
                <Form.Control
                  type="text"
                  value={addr.city || ''}
                  onChange={e => {
                    const val = e.target.value.replace(/[^A-Za-z\s]/g, '');
                    handleFieldChange(index, 'city', val);
                  }}
                  disabled={!isEditable}
                  maxLength={30}
                />
              </FloatingLabel>
            </Col>
            <Col md={4} className="mb-3">
              <div className="texbox-input-container">
                <Select
                  id={`state-${index}`}
                  label={<span>State</span>}
                  options={stateOptions}
                  defaultSelected={stateOptions.filter(opt => opt.value === addr.state)}
                  onChange={([selected]) => handleFieldChange(index, 'state', selected as string)}
                  disabled={!isEditable}
                  showAsFloatingLabel
                />
              </div>
            </Col>
            <Col md={4} className="mb-3">
              <FloatingLabel controlId={`zip-${index}`} label="Zip Code">
                <Form.Control
                  type="text"
                  value={addr.zipCode || ''}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    handleFieldChange(index, 'zipCode', val);
                  }}
                  disabled={!isEditable}
                  maxLength={9}
                />
              </FloatingLabel>
            </Col>
          </Row>
          <Row>
            <Col md="auto" className="mb-3">
              <Form.Check
                checked={addr.isForeign ?? false}
                onChange={e => handleFieldChange(index, 'isForeign', e.target.checked as any)}
                disabled={!isEditable}
                label="Is Foreign"
              />
            </Col>
            <Col md="auto" className="mb-3">
              <Form.Check
                checked={addr.isPaymentAddress ?? false}
                onChange={e => handleFieldChange(index, 'isPaymentAddress', e.target.checked as any)}
                disabled={!isEditable}
                label="Is Payment Address"
              />
            </Col>
            <Col md="auto" className="mb-3">
              <Form.Check
                checked={addr.isFormAddress ?? false}
                onChange={e => handleFieldChange(index, 'isFormAddress', e.target.checked as any)}
                disabled={!isEditable}
                label="Is Form Address"
              />
            </Col>
            <Col md="auto" className="mb-3">
              <Form.Check
                checked={addr.isPrimaryAddress ?? false}
                onChange={e => handleFieldChange(index, 'isPrimaryAddress', e.target.checked as any)}
                disabled={!isEditable}
                label="Is Primary Address"
              />
            </Col>
            <Col md="auto" className="mb-3">
              <Form.Check
                checked={addr.isPayee ?? false}
                onChange={e => handleFieldChange(index, 'isPayee', e.target.checked as any)}
                disabled={!isEditable}
                label="Is Payee"
              />
            </Col>
          </Row>

          <CarrierContactInfoSection
            mode={mode}
            address={addr}
            index={index}
            onChange={(field, value) => handleFieldChange(index, field, value)}
          />
          {isEditable && (
            <Row>
              <Col md={12} className="mb-2 d-flex justify-content-end">
                <Button type="button" variant="outline-danger" size="sm" onClick={() => handleRemove(index)} disabled={!canDelete}>
                  Remove
                </Button>
              </Col>
            </Row>
          )}
          {index < addresses.length - 1 && <hr />}
        </React.Fragment>
      ))}
    </>
  );
};

export default CarrierAddressList;
